import React from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, Wrench, BookOpen, Briefcase, Users, ArrowRight, CheckCircle2, Award, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import Layout from '../components/layout/Layout';
import { services, wiseProgram } from '../data/mock';

const iconMap = {
  GraduationCap,
  Wrench,
  BookOpen,
  Briefcase,
  Users,
  Award
};

const ServicesPage = () => {
  const fullServices = [
    {
      ...services[0],
      details: [
        "NATS enrollment for graduates/diploma holders",
        "NAPS registration for all trade apprentices",
        "Third Party Aggregator (TPA) support",
        "Complete documentation assistance",
        "Stipend management and compliance"
      ]
    },
    {
      ...services[2], // Skill Development (index changed due to WISE)
      details: [
        "Market-demand based training programs",
        "Future skills for Industry 4.0",
        "Practical hands-on training",
        "Certified courses with NSQF alignment",
        "Regular assessment and evaluation"
      ]
    },
    {
      ...services[3], // Education
      details: [
        "Vocational education linkage",
        "Formal education support",
        "Career counseling and guidance",
        "Academic to industry transition",
        "Continuous learning programs"
      ]
    },
    {
      ...services[4], // Placement
      details: [
        "Job placement post-training",
        "Network of 37+ partner establishments",
        "Resume building assistance",
        "Interview preparation",
        "Career progression support"
      ]
    },
    {
      ...services[5], // Mobilisation
      details: [
        "Skilled candidate sourcing",
        "Demand-based mobilization",
        "Quality assured workforce",
        "Pan-India reach",
        "Quick turnaround time"
      ]
    }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-700 to-slate-900">
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <span className="text-emerald-400 font-semibold text-sm uppercase tracking-wider">Our Services</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-2 mb-6">
              Comprehensive Skill Solutions
            </h1>
            <p className="text-xl text-blue-200 leading-relaxed">
              End-to-end services for skill development, apprenticeship, and placement across India.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          {/* Apprenticeship Service */}
          <div className="grid lg:grid-cols-2 gap-8 items-center mb-16">
            <div className="lg:order-1">
              <div className="relative overflow-hidden rounded-2xl">
                <img
                  src={services[0].image}
                  alt="Apprenticeship"
                  className="w-full h-80 object-cover"
                />
                <div className="absolute top-4 left-4">
                  <div className="w-14 h-14 rounded-xl bg-blue-700 flex items-center justify-center">
                    <GraduationCap className="w-7 h-7 text-white" />
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:order-2">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Apprenticeship</h2>
              <p className="text-slate-600 leading-relaxed mb-6">{services[0].description}</p>
              <ul className="space-y-3 mb-6">
                {["NATS enrollment for graduates/diploma holders", "NAPS registration for all trade apprentices", "Third Party Aggregator (TPA) support", "Complete documentation assistance", "Stipend management and compliance"].map((detail, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                    {detail}
                  </li>
                ))}
              </ul>
              <Link to="/apprenticeship">
                <Button className="bg-blue-700 hover:bg-blue-800 text-white">
                  Learn More
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>

          {/* WISE Program Section */}
          <div className="bg-gradient-to-br from-blue-50 to-emerald-50 rounded-3xl p-8 md:p-12 mb-16">
            <div className="grid lg:grid-cols-2 gap-8 items-start">
              <div>
                <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
                  <Award className="w-4 h-4" />
                  <span>Academic Program</span>
                </div>
                <h2 className="text-3xl font-bold text-slate-900 mb-4">{wiseProgram.title}</h2>
                <p className="text-slate-600 leading-relaxed mb-6">{wiseProgram.description}</p>
                
                <h3 className="font-semibold text-slate-900 mb-3">Key Highlights</h3>
                <ul className="space-y-3 mb-6">
                  {wiseProgram.highlights.map((highlight, i) => (
                    <li key={i} className="flex items-start gap-3 text-slate-700">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                      {highlight}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="space-y-6">
                <h3 className="font-semibold text-slate-900 text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-700" />
                  Program Details
                </h3>
                
                {wiseProgram.programs.map((program, index) => (
                  <div key={index} className="bg-white rounded-xl p-6 shadow-md">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-bold text-slate-900">{program.name}</h4>
                      <span className="bg-blue-100 text-blue-700 text-xs px-3 py-1 rounded-full font-medium">
                        {program.type}
                      </span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-slate-500 w-24">Eligibility:</span>
                        <span className="text-slate-700 font-medium">{program.eligibility}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-500 w-24">Age Criteria:</span>
                        <span className="text-slate-700 font-medium">{program.ageLimit}</span>
                      </div>
                    </div>
                  </div>
                ))}
                
                <Link to="/contact">
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
                    Enquire About WISE Program
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Other Services */}
          <div className="space-y-12">
            {fullServices.slice(1).map((service, index) => {
              const IconComponent = iconMap[service.icon];
              const isEven = index % 2 === 0;
              
              return (
                <div
                  key={service.id}
                  className={`grid lg:grid-cols-2 gap-8 items-center ${
                    isEven ? '' : 'lg:flex-row-reverse'
                  }`}
                >
                  <div className={isEven ? 'lg:order-1' : 'lg:order-2'}>
                    <div className="relative overflow-hidden rounded-2xl">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-80 object-cover"
                      />
                      <div className="absolute top-4 left-4">
                        <div className="w-14 h-14 rounded-xl bg-blue-700 flex items-center justify-center">
                          {IconComponent && <IconComponent className="w-7 h-7 text-white" />}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className={isEven ? 'lg:order-2' : 'lg:order-1'}>
                    <h2 className="text-3xl font-bold text-slate-900 mb-4">{service.title}</h2>
                    <p className="text-slate-600 leading-relaxed mb-6">{service.description}</p>
                    
                    <ul className="space-y-3 mb-6">
                      {service.details.map((detail, i) => (
                        <li key={i} className="flex items-center gap-3 text-slate-700">
                          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                    
                    <Link to="/contact">
                      <Button className="bg-blue-700 hover:bg-blue-800 text-white">
                        Learn More
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Process */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">How We Work</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2">
              Our Service Process
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: 1, title: "Connect", description: "Register with us through our online portal or visit our office" },
              { step: 2, title: "Assess", description: "We evaluate your requirements and match with suitable programs" },
              { step: 3, title: "Train", description: "Undergo skill training or apprenticeship with our partners" },
              { step: 4, title: "Place", description: "Get placed in reputed organizations with our support" }
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-xl p-6 shadow-md text-center h-full">
                  <div className="w-12 h-12 rounded-full bg-blue-700 text-white font-bold text-xl flex items-center justify-center mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.description}</p>
                </div>
                {index < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-6 h-6 text-slate-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-emerald-600 to-emerald-800">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Start Your Skill Journey?
          </h2>
          <p className="text-emerald-100 text-lg mb-8">
            Register today and take the first step towards a brighter future.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/careers">
              <Button size="lg" className="bg-white text-emerald-700 hover:bg-emerald-50">
                Register as Candidate
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-emerald-700">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ServicesPage;
